import React, { useRef, useState, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import moment from "moment";

import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";

import * as QuoteActions from "../Store/Actions/Quote";
import { formatNumberCurrency } from "./Utils";
import { useMediaQuery } from "react-responsive";
import { printQuote, viewQuotePdf, fetchQuotePdfUrl } from "../Utils/quotePdf";
import { descreverCambio } from "../Utils/cambioJanela";
import { publicBaseUrl } from "../Utils/publicUrl";
import imagePlaceHolder from "../Assets/Images/noimg.jpg";
import {
	QUOTE_STATUS,
	QUOTE_STATUS_OPTIONS,
	quoteStatusLabel,
	quoteStatusSeverity,
	quartoTemCafe,
	quotePodeEditar,
	quotePodeExcluir,
	isCotacaoF2
} from "../Constants/quote";
import { FEATURE_FLAGS } from "../Config/environment";
import QuoteFase2Panel from "./QuoteFase2Panel";

/**
 * QuoteDetailModal — detalhe da cotação em modal (largura contida).
 * Aberto por Redux (quoteReducer.detailOpen) a partir do atalho do header ou da
 * lista admin. Agrupa itens por período, permite remover item, alterar status e
 * excluir a cotação. Mostra o nome da agência quando o usuário é master.
 */
const QuoteDetailModal = (props) => {
	const { selectedQuote, detailOpen, masterUser } = props;
	const { closeQuoteDetail, removeQuoteItem, setQuoteStatus, deleteQuote, renameQuote, refreshQuoteValues } = props;
	const toast = useRef(null);

	const quote = selectedQuote;
	// Link público de visualização do hotel (nova aba): /hotel/:hotelViewToken?a=:agencyViewToken.
	const hotelViewUrl = (it) => {
		const t = it && it.hotelViewToken;
		if (!t) return null;
		return `${publicBaseUrl()}/hotel/${t}${quote?.agencyViewToken ? `?a=${encodeURIComponent(quote.agencyViewToken)}` : ""}`;
	};
	const editavel = quotePodeEditar(quote?.status);
	const excluivel = quotePodeExcluir(quote?.status);

	// Edição inline do nome da cotação.
	const [editingName, setEditingName] = useState(false);
	const [nameDraft, setNameDraft] = useState("");
	const [busy, setBusy] = useState(false); // ação no backend em andamento
	const [pendingStatus, setPendingStatus] = useState(null); // 'enviada' | 'convertida'
	const [contato, setContato] = useState({ nome: "", email: "", documento: "", telefone: "", isForeigner: false });
	const [bookingIdInput, setBookingIdInput] = useState("");
	const [pdfOpen, setPdfOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1023px)" });

	// Sai do modo edição ao trocar de cotação ou fechar o modal.
	useEffect(() => {
		setEditingName(false);
		setPendingStatus(null);
		setPdfOpen(false);
		setPdfUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
	}, [quote?.id, detailOpen]);

	// Trava o scroll da página-mãe enquanto o modal está aberto.
	useEffect(() => {
		if (!detailOpen) return undefined;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => { document.body.style.overflow = prev; };
	}, [detailOpen]);

	// Enquanto o novo conteúdo carrega (troca de cotação), mostra loading em vez do
	// conteúdo da cotação anterior — elimina o "flash". Para cotações mock (síncronas)
	// o quote já vem setado antes de abrir, então isto nem chega a renderizar.
	if (detailOpen && !quote) {
		return (
			<Dialog header="Cotação" visible onHide={closeQuoteDetail}
				style={{ width: "min(820px, 96vw)" }} dismissableMask blockScroll>
				<div style={{ padding: "3rem 1.5rem", textAlign: "center" }}>
					<ProgressSpinner style={{ width: 44, height: 44 }} strokeWidth="4" />
					<p style={{ color: "#6B6B6B", marginTop: "1rem" }}>Carregando cotação…</p>
				</div>
			</Dialog>
		);
	}

	const startEditName = () => {
		setNameDraft(quote?.nome || "");
		setEditingName(true);
	};
	const saveName = () => {
		const n = nameDraft.trim();
		if (n && n !== quote.nome) {
			setBusy(true);
			renameQuote(quote.id, n)
				.then(() => toast.current?.show({ severity: "success", summary: "Nome atualizado", life: 2500 }))
				.catch((e) => toast.current?.show({ severity: "error", summary: "Erro ao renomear", detail: e?.response?.data?.errorMessage || "Tente novamente.", life: 5000 }))
				.finally(() => setBusy(false));
		}
		setEditingName(false);
	};

	const itemValue = (it) =>
		it.precoBRL !== null && it.precoBRL !== undefined
			? { v: it.precoBRL, c: "BRL" }
			: { v: it.precoUSD || 0, c: "USD" };

	const fmtPeriodo = (p) =>
		p?.checkIn
			? `${moment(p.checkIn).format("DD/MM/YYYY")} – ${moment(p.checkOut).format("DD/MM/YYYY")}`
			: "Período não definido";

	const groups = quote
		? Object.values(
				(quote.itens || []).reduce((acc, it) => {
					const key = `${it.periodo?.checkIn}_${it.periodo?.checkOut}`;
					if (!acc[key]) acc[key] = { periodo: it.periodo, itens: [] };
					acc[key].itens.push(it);
					return acc;
				}, {})
		  ).sort((a, b) =>
				String(a.periodo?.checkIn || "").localeCompare(String(b.periodo?.checkIn || ""))
		  )
		: [];

	const itineraryEstimate = groups.reduce((acc, g) => {
		const cheapest = g.itens.reduce((min, it) => Math.min(min, itemValue(it).v), Infinity);
		return cheapest !== Infinity ? acc + cheapest : acc;
	}, 0);

	const confirmRemove = (event, item) => {
		confirmPopup({
			target: event.currentTarget,
			message: "Remover este item da cotação?",
			icon: "pi pi-exclamation-triangle",
			acceptLabel: "Remover",
			rejectLabel: "Cancelar",
			accept: () => {
				setBusy(true);
				removeQuoteItem(quote.id, item.id)
					.then(() => toast.current?.show({ severity: "success", summary: "Item removido", life: 3000 }))
					.catch((e) => toast.current?.show({ severity: "error", summary: "Erro ao remover", detail: e?.response?.data?.errorMessage || "Tente novamente.", life: 5000 }))
					.finally(() => setBusy(false));
			}
		});
	};

	const confirmDeleteQuote = (event) => {
		confirmPopup({
			target: event.currentTarget,
			message: "Excluir esta cotação inteira? Esta ação não pode ser desfeita.",
			icon: "pi pi-trash",
			acceptClassName: "p-button-danger",
			acceptLabel: "Excluir",
			rejectLabel: "Cancelar",
			accept: () => {
				setBusy(true);
				deleteQuote(quote.id)
					.then(() => toast.current?.show({ severity: "success", summary: "Cotação excluída", life: 3000 }))
					.catch((e) => toast.current?.show({ severity: "error", summary: "Erro ao excluir", detail: e?.response?.data?.errorMessage || "Tente novamente.", life: 5000 }))
					.finally(() => setBusy(false));
			}
		});
	};

	const applyStatus = (status, extra = {}) => {
		setBusy(true);
		setQuoteStatus(quote.id, status, extra)
			.then(() => {
				toast.current?.show({ severity: "info", summary: "Status atualizado", detail: quoteStatusLabel(status), life: 3000 });
				setPendingStatus(null);
			})
			.catch((err) => toast.current?.show({ severity: "error", summary: "Erro ao mudar status", detail: err?.response?.data?.errorMessage || "Tente novamente.", life: 5000 }))
			.finally(() => setBusy(false));
	};

	const onChangeStatus = (e) => {
		const v = e.value;
		if (!v || v === quote.status) return;
		// Enviada e Convertida exigem dados extras (CRM): abrir formulário.
		if (v === QUOTE_STATUS.ENVIADA) {
			setContato({ nome: "", email: "", documento: "", telefone: "", isForeigner: false });
			setPendingStatus(QUOTE_STATUS.ENVIADA);
			return;
		}
		if (v === QUOTE_STATUS.CONVERTIDA) {
			setBookingIdInput("");
			setPendingStatus(QUOTE_STATUS.CONVERTIDA);
			return;
		}
		applyStatus(v);
	};

	const submitContato = () => {
		if (!contato.nome.trim() || !contato.email.trim() || !contato.documento.trim()) {
			toast.current?.show({ severity: "warn", summary: "Preencha os dados do contato", detail: "Nome, e-mail e documento são obrigatórios.", life: 4000 });
			return;
		}
		applyStatus(QUOTE_STATUS.ENVIADA, {
			contato: {
				nome: contato.nome.trim(),
				email: contato.email.trim(),
				documento: contato.documento.trim(),
				telefone: contato.telefone.trim() || undefined,
				isForeigner: !!contato.isForeigner
			}
		});
	};

	const submitBooking = () => {
		const id = bookingIdInput.trim();
		if (!id) {
			toast.current?.show({ severity: "warn", summary: "Informe o ID da reserva", detail: "O ID da reserva (bookingId) é obrigatório para converter.", life: 4000 });
			return;
		}
		applyStatus(QUOTE_STATUS.CONVERTIDA, { bookingId: Number(id) || id });
	};

	const handleDownloadPdf = () => {
		setBusy(true);
		printQuote(quote)
			.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao gerar PDF", detail: "Não foi possível gerar o PDF agora. Tente novamente.", life: 5000 }))
			.finally(() => setBusy(false));
	};

	const handleViewPdf = () => {
		setBusy(true);
		// Mobile: iframe de PDF é instável (iOS Safari) → abre em nova aba/download.
		if (isTabletOrMobile) {
			viewQuotePdf(quote)
				.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao abrir o PDF", detail: "Não foi possível abrir o PDF agora. Tente novamente.", life: 5000 }))
				.finally(() => setBusy(false));
			return;
		}
		// Desktop: visualiza dentro de uma modal (iframe com o visualizador nativo).
		fetchQuotePdfUrl(quote)
			.then((url) => { setPdfUrl(url); setPdfOpen(true); })
			.catch(() => toast.current?.show({ severity: "error", summary: "Erro ao abrir o PDF", detail: "Não foi possível abrir o PDF agora. Tente novamente.", life: 5000 }))
			.finally(() => setBusy(false));
	};

	const closePdfViewer = () => {
		setPdfOpen(false);
		setPdfUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
	};

	// Re-cota valores/disponibilidade + câmbio (só rascunho) e mostra o resumo.
	// Shift+clique: força todos os itens como indisponíveis (teste de UI; só DEVEL — prod ignora).
	const handleRefresh = (e) => {
		setBusy(true);
		const opts = e?.shiftKey ? { forceUnavailable: true } : {};
		refreshQuoteValues(quote.id, opts)
			.then((res) => {
				const r = res?.resumo || {};
				const parts = [];
				if (r.atualizados != null) parts.push(`${r.atualizados} atualizado(s)`);
				if (r.indisponiveis) parts.push(`${r.indisponiveis} indisponível(is)`);
				if (r.naoRecotaveis) parts.push(`${r.naoRecotaveis} não-recotável(is)`);
				if (r.cambioAtualizado) parts.push("câmbio atualizado");
				toast.current?.show({ severity: r.indisponiveis ? "warn" : "success", summary: "Valores atualizados", detail: parts.join(" · ") || "Cotação atualizada.", life: 6000 });
			})
			.catch((e) => toast.current?.show({ severity: "error", summary: "Erro ao atualizar", detail: e?.response?.data?.errorMessage || "Não foi possível re-cotar agora. Tente novamente.", life: 6000 }))
			.finally(() => setBusy(false));
	};

	const headerEl = quote ? (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", paddingRight: "1rem", flexWrap: "wrap" }}>
			{editingName ? (
				<div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flex: 1, minWidth: "12rem" }}>
					<InputText
						value={nameDraft}
						onChange={(e) => setNameDraft(e.target.value)}
						autoFocus
						style={{ flex: 1 }}
						onKeyDown={(e) => {
							if (e.key === "Enter") saveName();
							if (e.key === "Escape") setEditingName(false);
						}}
					/>
					<Button icon="pi pi-check" rounded text aria-label="Salvar nome" onClick={saveName} loading={busy} disabled={busy} />
					<Button icon="pi pi-times" rounded text severity="secondary" aria-label="Cancelar" onClick={() => setEditingName(false)} disabled={busy} />
				</div>
			) : (
				<span style={{ fontWeight: 700, color: "#2E0249", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
					{quote.nome}
					{editavel && <Button icon="pi pi-pencil" rounded text size="small" aria-label="Editar nome" onClick={startEditName} disabled={busy} />}
				</span>
			)}
			<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
				{!editavel && <small style={{ color: "#9aa0a6", fontSize: "0.78rem" }}>Somente leitura · volte para Rascunho para alterar</small>}
				{excluivel && <Button label="Excluir cotação" icon="pi pi-trash" text severity="danger" size="small" onClick={confirmDeleteQuote} loading={busy} disabled={busy} />}
			</div>
		</div>
	) : null;

	// Cotação Fase 2 (atrás da flag): renderiza o painel novo no lugar do CRM legado.
	// Prod (flag OFF) e cotações antigas seguem o fluxo abaixo inalterado.
	if (FEATURE_FLAGS.COTACAO_FASE2 && isCotacaoF2(quote)) {
		return (
			<Dialog header={quote?.nome || "Cotação"} visible={detailOpen && !!quote} onHide={closeQuoteDetail}
				style={{ width: "min(820px, 96vw)" }} dismissableMask blockScroll>
				{/* Em modo mock, habilita o bloco "dev" de simular pagamento para testar
				    o funil inteiro na estrutura real. Some sozinho quando o BE integrar. */}
				<QuoteFase2Panel allowDevSim={QuoteActions.isF2MockMode()} />
			</Dialog>
		);
	}

	return (
		<>
			<Toast ref={toast} />
			<ConfirmPopup />
			<Dialog
				header={headerEl}
				footer={
					quote ? (
						<div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
							<div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
								{/* Re-cotar valores/disponibilidade ao vivo (só rascunho). */}
								{quotePodeEditar(quote.status) && (quote.itens || []).length > 0 && (
									<Button label="Atualizar preços e disponibilidade" icon="pi pi-refresh" onClick={handleRefresh} loading={busy} disabled={busy} style={{ background: "#814DE5", borderColor: "#814DE5" }} />
								)}
								{(quote.itens || []).length > 0 && (
									<SplitButton
										label="Visualizar PDF"
										icon="pi pi-eye"
										outlined
										className="pdfSplit"
										onClick={handleViewPdf}
										disabled={busy}
										model={[{ label: "Baixar PDF", icon: "pi pi-download", command: handleDownloadPdf }]}
									/>
								)}
								<Button label="Fechar" text onClick={closeQuoteDetail} disabled={busy} />
							</div>
						</div>
					) : null
				}
				visible={detailOpen && !!quote}
				onHide={closeQuoteDetail}
				style={{ width: "min(912px, 96vw)" }}
				dismissableMask
				blockScroll
			>
				{quote && (
					<>
						{/* Meta + status */}
						<div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
							<div style={{ textAlign: "left", fontSize: "0.8rem", color: "#6B6B6B" }}>
								Criada por {quote.createdBy?.nome || "—"}
								{masterUser && quote.createdBy?.agencyName ? ` · Agência: ${quote.createdBy.agencyName}` : ""}
								<br />
								{moment(quote.criadoEm).format("DD/MM/YYYY HH:mm")}
							</div>
							<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
								<Tag value={quoteStatusLabel(quote.status)} severity={quoteStatusSeverity(quote.status)} />
								<Dropdown
									value={quote.status}
									options={QUOTE_STATUS_OPTIONS}
									optionLabel="label"
									optionValue="value"
									onChange={onChangeStatus}
									placeholder="Status"
									style={{ minWidth: "11rem" }}
									disabled={busy}
								/>
							</div>
						</div>

						{quote.cambioSnapshot?.usdBrl && (
							<div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "8px", padding: "0.6rem 0.8rem", marginBottom: "1rem", fontSize: "0.78rem", color: "#7A5C00" }}>
								<i className="pi pi-exclamation-triangle" style={{ marginTop: "2px" }} />
								{/* A data saía com `moment(...).format("DD/MM/YYYY")`, que PERDE a hora — e a hora
								    é justamente o que importa aqui: a taxa da SMI é publicada uma vez por dia e a
								    validade atravessa a virada. `descreverCambio` monta a frase a partir de
								    `atualizadoEm`/`validoAte`/`congelado`, que o BE passou a mandar em 2026-09-04. */}
								<span>Valores em BRL convertidos pelo câmbio informado. {descreverCambio(quote.cambioSnapshot)?.texto} Sujeitos a alteração e disponibilidade — use “Atualizar valores e disponibilidade”.</span>
							</div>
						)}

						{groups.length === 0 && (
							<div style={{ color: "#6B6B6B", textAlign: "center", padding: "1.5rem 0" }}>
								Esta cotação ainda não tem itens.
							</div>
						)}

						{groups.map((g, gi) => {
							const valores = g.itens.map((it) => itemValue(it).v);
							const min = Math.min(...valores);
							const max = Math.max(...valores);
							return (
								<div key={gi} style={{ border: "1px solid #e3e3e3", borderRadius: "12px", padding: "0.85rem", marginBottom: "0.85rem" }}>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem", flexWrap: "wrap", gap: "0.4rem" }}>
										<div style={{ fontWeight: 600, color: "#2E0249", fontSize: "0.92rem" }}>
											<i className="pi pi-calendar" style={{ marginRight: "0.4rem" }} />
											{fmtPeriodo(g.periodo)}
										</div>
										<div style={{ fontSize: "0.78rem", color: "#6B6B6B" }}>
											{g.itens.length} opção(ões)
											{g.itens.length > 1 && (
												<span> · de {formatNumberCurrency(min, "BRL")} a {formatNumberCurrency(max, "BRL")}</span>
											)}
										</div>
									</div>

									{g.itens.map((it) => {
										const val = itemValue(it);
										return (
											<div key={it.id} style={{ display: "flex", gap: "0.75rem", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 0", borderTop: "1px dashed #eee" }}>
												<div style={{ display: "flex", gap: "0.6rem", alignItems: "center", textAlign: "left", minWidth: 0 }}>
													<img
														src={it.hotel?.foto || imagePlaceHolder}
														onError={(e) => { e.target.src = imagePlaceHolder; }}
														alt=""
														style={{ width: "128px", height: "96px", objectFit: "cover", borderRadius: "8px", flexShrink: 0 }}
													/>
													<div style={{ minWidth: 0 }}>
														<div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
															{hotelViewUrl(it) ? (
																<a href={hotelViewUrl(it)} target="_blank" rel="noopener noreferrer" style={{ color: "#814DE5", textDecoration: "none" }} title="Ver detalhes do hotel">
																	{it.hotel?.nome || "Hotel"} <i className="pi pi-external-link" style={{ fontSize: "0.7rem" }} />
																</a>
															) : (it.hotel?.nome || "Hotel")}
														</div>
														<div style={{ fontSize: "0.78rem", color: "#6B6B6B" }}>
															{[it.hotel?.cidade, it.hotel?.pais].filter(Boolean).join(", ")}
														</div>
														<div style={{ fontSize: "0.78rem", color: "#6B6B6B" }}>
															{it.quarto?.nome}
															{it.quarto?.regime ? ` · ${it.quarto.regime}` : ""}
															{` · ${it.pax?.adultos || 0} adulto(s)`}
															{it.pax?.criancas ? ` · ${it.pax.criancas} criança(s)` : ""}
														</div>
														<div style={{ fontSize: "0.74rem", marginTop: "0.15rem", color: quartoTemCafe(it.quarto) ? "#1e6b3a" : "#9aa0a6" }}>
															<i className={quartoTemCafe(it.quarto) ? "pi pi-check" : "pi pi-times"} style={{ fontSize: "0.7rem", marginRight: "0.25rem" }} />
															{quartoTemCafe(it.quarto) ? "Café da manhã incluído" : "Sem café da manhã"}
														</div>
													</div>
												</div>
												<div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
													{it.disponivel === false && (
														<div style={{ marginBottom: "0.25rem" }}>
															<Tag value="Indisponível" severity="danger" />
														</div>
													)}
													<div style={{ fontWeight: 700, color: "#2E0249", textDecoration: it.disponivel === false ? "line-through" : "none", opacity: it.disponivel === false ? 0.55 : 1 }}>{formatNumberCurrency(val.v, val.c)}</div>
													{it.precoBRL !== null && it.precoBRL !== undefined && (
														<div style={{ fontSize: "0.72rem", color: "#6B6B6B" }}>{formatNumberCurrency(it.precoUSD, "USD")}</div>
													)}
													{editavel && <Button icon="pi pi-trash" rounded text severity="danger" aria-label="Remover item" onClick={(e) => confirmRemove(e, it)} disabled={busy} />}
												</div>
											</div>
										);
									})}
								</div>
							);
						})}

						{/* Totalizador (estimativa de roteiro) removido da visualização a pedido — manter comentado. */}
						{false && groups.length > 0 && (
							<div style={{ marginTop: "0.25rem", padding: "0.85rem 1rem", background: "#F2F4F7", borderRadius: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
								<div style={{ fontSize: "0.82rem", color: "#6B6B6B" }}>
									Estimativa de roteiro (1 opção mais barata por período)
								</div>
								<div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#814DE5" }}>
									{formatNumberCurrency(itineraryEstimate, "BRL")}
								</div>
							</div>
						)}
					</>
				)}
			</Dialog>

			{/* Transição de status que exige dados (CRM): Enviada (contato) / Convertida (bookingId) */}
			<Dialog
				header={pendingStatus === QUOTE_STATUS.CONVERTIDA ? "Converter cotação em reserva" : "Marcar cotação como enviada"}
				visible={!!pendingStatus}
				onHide={() => { if (!busy) setPendingStatus(null); }}
				style={{ width: "min(480px, 95vw)" }}
				dismissableMask={!busy}
			>
				{pendingStatus === QUOTE_STATUS.ENVIADA && (
					<div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
						<div style={{ fontSize: "0.85rem", color: "#6B6B6B" }}>
							Informe o contato do cliente — será registrado no CRM ao enviar a cotação.
						</div>
						<div>
							<label style={{ display: "block", fontSize: "0.8rem", marginBottom: "0.25rem", fontWeight: 600 }}>Nome *</label>
							<InputText value={contato.nome} onChange={(e) => setContato({ ...contato, nome: e.target.value })} style={{ width: "100%" }} disabled={busy} />
						</div>
						<div>
							<label style={{ display: "block", fontSize: "0.8rem", marginBottom: "0.25rem", fontWeight: 600 }}>E-mail *</label>
							<InputText value={contato.email} onChange={(e) => setContato({ ...contato, email: e.target.value })} style={{ width: "100%" }} disabled={busy} />
						</div>
						<div>
							<label style={{ display: "block", fontSize: "0.8rem", marginBottom: "0.25rem", fontWeight: 600 }}>{contato.isForeigner ? "Documento (passaporte) *" : "CPF/CNPJ *"}</label>
							<InputText value={contato.documento} onChange={(e) => setContato({ ...contato, documento: e.target.value })} style={{ width: "100%" }} disabled={busy} />
						</div>
						<div>
							<label style={{ display: "block", fontSize: "0.8rem", marginBottom: "0.25rem", fontWeight: 600 }}>Telefone</label>
							<InputText value={contato.telefone} onChange={(e) => setContato({ ...contato, telefone: e.target.value })} style={{ width: "100%" }} disabled={busy} />
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<Checkbox inputId="ctForeigner" checked={contato.isForeigner} onChange={(e) => setContato({ ...contato, isForeigner: e.checked })} disabled={busy} />
							<label htmlFor="ctForeigner" style={{ cursor: "pointer", fontSize: "0.9rem" }}>Estrangeiro (sem CPF)</label>
						</div>
						<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
							<Button label="Cancelar" text onClick={() => setPendingStatus(null)} disabled={busy} />
							<Button label="Marcar como enviada" icon="pi pi-send" onClick={submitContato} loading={busy} disabled={busy} />
						</div>
					</div>
				)}
				{pendingStatus === QUOTE_STATUS.CONVERTIDA && (
					<div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
						<div style={{ fontSize: "0.85rem", color: "#6B6B6B" }}>
							Informe o ID da reserva (booking) gerada a partir desta cotação para vincular no funil.
						</div>
						<div>
							<label style={{ display: "block", fontSize: "0.8rem", marginBottom: "0.25rem", fontWeight: 600 }}>ID da reserva *</label>
							<InputText value={bookingIdInput} onChange={(e) => setBookingIdInput(e.target.value)} keyfilter="int" style={{ width: "100%" }} disabled={busy} />
						</div>
						<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.25rem" }}>
							<Button label="Cancelar" text onClick={() => setPendingStatus(null)} disabled={busy} />
							<Button label="Converter" icon="pi pi-check" onClick={submitBooking} loading={busy} disabled={busy} />
						</div>
					</div>
				)}
			</Dialog>

			{/* Visualizador de PDF em modal (desktop) — iframe com o visualizador nativo */}
			<Dialog
				header="Visualizar PDF da cotação"
				visible={pdfOpen}
				onHide={closePdfViewer}
				style={{ width: "min(960px, 96vw)", height: "92vh" }}
				contentStyle={{ height: "100%", padding: 0 }}
				maximizable
				dismissableMask
			>
				{pdfUrl ? (
					<iframe
						title="PDF da cotação"
						src={pdfUrl}
						style={{ width: "100%", height: "100%", minHeight: "70vh", border: 0 }}
					/>
				) : null}
			</Dialog>
		</>
	);
};

const mapStateToProps = (store) => ({
	selectedQuote: store.quoteReducer.selectedQuote,
	detailOpen: store.quoteReducer.detailOpen,
	masterUser: store.appReducer.masterUser
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...QuoteActions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(QuoteDetailModal);
